import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';
import Table from 'react-bootstrap/Table';
import { useTranslation } from 'react-i18next';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';

import {
  getUISetting,
  getCurrencyFormatter,
  getDateFormatter,
} from 'reducers/configs/settings';
import {
  getCustomerRewardPointsSelector,
  getUseCustomerRegistry,
} from 'reducers/customerSearch';
import './CustomerTable.css';
import { Customer } from 'types/Customer';
import { Warehouse } from 'types/Warehouse';

const personFieldsOnly = ['birthday'];

const getCustomerCardFields = (customerType: 'PERSON' | 'COMPANY') => state => {
  const fields = getUseCustomerRegistry(state)
    ? [
        { key: 'customerCardNumber', name: 'Card number' },
        { key: 'groupName', name: 'Group' },
        { key: 'birthday', name: 'Birthday' },
        { key: 'email', name: 'E-mail' },
        { key: 'phone', name: 'Phone' },
        { key: 'mobile', name: 'Mobile' },
        { key: 'addresses', name: 'Address' },
        { key: 'rewardPoints', name: 'Reward points' },
        { key: 'code', name: 'Code' },
        { key: 'vatNumber', name: 'VAT number' },
        { key: 'homeStoreID', name: 'Home store' },
      ]
    : [
        { key: 'customerCardNumber', name: 'Card number' },
        { key: 'groupName', name: 'Group' },
        { key: 'birthday', name: 'Birthday' },
        { key: 'email', name: 'E-mail' },
        { key: 'phone', name: 'Phone' },
        { key: 'mobile', name: 'Mobile' },
        { key: 'addresses', name: 'Address' },
        { key: 'rewardPoints', name: 'Reward points' },
        { key: 'availableCredit', name: 'Store credit' },
        { key: 'code', name: 'Code' },
        { key: 'vatNumber', name: 'VAT number' },
        { key: 'homeStoreID', name: 'Home store' },
        { key: 'creditLimit', name: 'Credit limit' },
      ];
  const filteredFields = fields.filter(({ key }) =>
    customerType === 'COMPANY' ? !personFieldsOnly.includes(key) : key,
  );
  return {
    leftFields: filteredFields.slice(0, 6),
    rightFields: filteredFields.slice(6, 13),
  };
};

const CustomerTable: React.FC<{
  customer: Customer;
  homeStore: Warehouse | null;
}> = ({ customer, homeStore }) => {
  const formatDate = useSelector(getDateFormatter);
  const { customerType } = customer;
  const { leftFields, rightFields } = useSelector(
    getCustomerCardFields(customerType),
  );

  const currencyFormat = useSelector(getCurrencyFormatter);
  const rewardPoints = useSelector(getCustomerRewardPointsSelector);
  const hideHomeStore = useSelector(
    getUISetting('isCustomerHomeStoreDisabled'),
  );

  const { t } = useTranslation('customer');
  const homeStorename = homeStore?.name ?? '';

  const getRightFieldData = useCallback(
    (key: string) => {
      switch (key) {
        case 'addresses':
          return customer?.[key]?.[0]?.address;
        case 'homeStoreID':
          return homeStorename;
        case 'availableCredit':
          return currencyFormat(customer[key]);
        case 'rewardPoints':
          return rewardPoints;
        default:
          return customer[key];
      }
    },
    [currencyFormat, customer, homeStorename, rewardPoints],
  );

  const customerBirthdayVisualizer = (date: string) => {
    if (date === '0000-00-00') {
      return '';
    }
    // Unspecified year is stored as `9999` and should be displayed as `----`
    return formatDate(date)?.replace(/9999/, '----');
  };

  const getLeftFields = useCallback(
    (key: string) => {
      switch (key) {
        case 'addresses':
          return customer?.[key]?.[0]?.address;
        case 'birthday':
          return customerBirthdayVisualizer(customer[key]);
        default:
          return customer[key];
      }
    },
    [customer, customerBirthdayVisualizer],
  );

  return (
    <Row
      className="customer-tables-container"
      data-testid="customer-table-container"
    >
      <Col sm={12} md={12} lg={6}>
        <Table borderless size="sm">
          <tbody>
            {leftFields.map(({ key, name }) => (
              <tr data-testid={name} key={name}>
                <td>
                  <strong data-testid={key}>
                    {t(`customerView.fields.${key}`)}
                  </strong>
                </td>
                <td
                  data-testid={customer[key]}
                  style={{ textAlign: 'right', wordBreak: 'break-all' }}
                >
                  {getLeftFields(key)}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Col>
      <Col sm={12} md={12} lg={6}>
        <Table borderless size="sm">
          <tbody>
            {rightFields.map(({ key, name }) => {
              if (key === 'vatNumber' && customer.customerType !== 'COMPANY') {
                return null;
              }
              if (key === 'homeStoreID' && hideHomeStore) {
                return null;
              }
              return (
                <tr data-testid={name} key={name}>
                  <td>
                    <strong data-testid={key}>
                      {t(`customerView.fields.${key}`)}
                    </strong>
                  </td>
                  <td
                    data-testid={customer[key]}
                    style={{ textAlign: 'right', wordBreak: 'break-all' }}
                  >
                    {getRightFieldData(key)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </Col>
    </Row>
  );
};

export default CustomerTable;
